import React, { useState, useEffect } from 'react';
import Calendar from 'react-calendar';
import { db } from '../../firebase';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, deleteDoc, doc } from "firebase/firestore";
import 'react-calendar/dist/Calendar.css';
import logger from '../../utils/logger';
import { TrashIcon, PencilIcon, ClockIcon, PlusCircleIcon } from '@heroicons/react/24/outline';
import RescheduleAppointmentModal from './Modals/RescheduleAppointmentModal';
import EditNotesModal from './Modals/EditNotesModal';


const AppointmentCalendar = () => {
    const [date, setDate] = useState(new Date());
    const [appointments, setAppointments] = useState([]);
    const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);
    const [selectedAppointment, setSelectedAppointment] = useState(null);
    const [isEditNotesModalOpen, setIsEditNotesModalOpen] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchAppointments = async () => {
            const q = query(collection(db, "appointments"), where("status", "==", "Scheduled"));
            const querySnapshot = await getDocs(q);
            const fetchedAppointments = [];
            querySnapshot.forEach((doc) => {
                let data = doc.data();
                // Convert the date string to a Date object for easy comparison
                data.date = new Date(data.date);
                fetchedAppointments.push({...data, id: doc.id});
            });
            setAppointments(fetchedAppointments);
        };
        fetchAppointments();
    }, []);

    const onChange = (newDate) => {
        setDate(newDate);
    };

   
    const handleDelete = async (appointmentId) => {
        try {
            const appointmentRef = doc(db, "appointments", appointmentId);
            await deleteDoc(appointmentRef);
            setAppointments(appointments.filter(appointment => appointment.id !== appointmentId));
            // Show a success message
            logger.info("Appointment deleted successfully");
        } catch (error) {
            logger.error("Error deleting appointment: ", error);
            // Handle the error accordingly, e.g., show an error message
        }
    };


    const handleOpenRescheduleModal = (appointment) => {
        setSelectedAppointment(appointment);
        setIsRescheduleModalOpen(true);
    };
  
    const handleOpenEditNotesModal = (appointment) => {
        setSelectedAppointment(appointment);
        setIsEditNotesModalOpen(true);
    };

    const handleJobCardCreate = (appointment) => {
        // Prepare the data required by JobCard
        const jobCardData = {
            vehicle: {
                id: appointment.vehicleId,
                vehicleMake: appointment.vehicleMake,
                vehicleModel: appointment.vehicleModel,
                year: appointment.vehicleYear,
                registrationNumber: appointment.vehicleRegistrationNumber,
                customerId: appointment.customerId // If needed
            },
            customer: {
                name: appointment.customerName,
                id: appointment.customerId // Assuming you have customer ID
                // Add other customer details if available and required
            }
        };
    
        // Navigate to JobCard with the data
        navigate('/vehicles/jobs', { state: { vehicle: jobCardData.vehicle, customer: jobCardData.customer } });
    };


    const filteredAppointments = appointments.filter(appointment =>
        appointment.date.toDateString() === date.toDateString()
    );

    return (
        <>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* Calendar Section */}
            <div className="bg-gradient-to-br from-gray-50 to-amber-50 p-6 rounded-2xl shadow-lg border border-amber-100">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <ClockIcon className="h-5 w-5 text-amber-600" />
                    Select Date
                </h3>
                <p className="text-sm text-gray-600 mb-4">Click on a date to view scheduled appointments</p>
                <Calendar
                    onChange={onChange}
                    value={date}
                    className="mx-auto rounded-xl shadow-md border-0"
                />
            </div>

            {/* Appointments List Section */}
            <div className="bg-gradient-to-br from-gray-50 to-orange-50 p-6 rounded-2xl shadow-lg border border-orange-100">
                <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <ClockIcon className="h-5 w-5 text-orange-600" />
                    Appointments for {date.toDateString()}
                </h2>

                {filteredAppointments.length === 0 ? (
                    <div className="text-center py-8">
                        <ClockIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500 font-medium">No appointments scheduled for this date</p>
                    </div>
                ) : (
                    <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
                        {filteredAppointments.map((appointment) => (
                            <div key={appointment.id} className="bg-white p-4 rounded-xl shadow-md border border-gray-200 hover:shadow-lg transition-all duration-200">

                                {/* Appointment Header */}
                                <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-100">
                                    <div className="flex items-center gap-2">
                                        <ClockIcon className="h-5 w-5 text-amber-600" />
                                        <span className="text-sm font-bold text-gray-900">{appointment.time}</span>
                                    </div>
                                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                                        Scheduled
                                    </span>
                                </div>

                                {/* Appointment Details */}
                                <div className="space-y-2 mb-4">
                                    <div className="flex items-start">
                                        <span className="text-xs font-semibold text-gray-500 w-24">Customer:</span>
                                        <span className="text-sm font-medium text-gray-900">{appointment.customerName}</span>
                                    </div>
                                    <div className="flex items-start">
                                        <span className="text-xs font-semibold text-gray-500 w-24">Vehicle:</span>
                                        <span className="text-sm text-gray-700">
                                            {appointment.vehicleMake} {appointment.vehicleModel} ({appointment.vehicleYear})
                                        </span>
                                    </div>
                                    <div className="flex items-start">
                                        <span className="text-xs font-semibold text-gray-500 w-24">Reg Number:</span>
                                        <span className="text-sm font-mono text-gray-700">{appointment.vehicleRegistrationNumber}</span>
                                    </div>
                                    {appointment.notes && (
                                        <div className="flex items-start">
                                            <span className="text-xs font-semibold text-gray-500 w-24">Notes:</span>
                                            <span className="text-sm text-gray-700">{appointment.notes}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Action Buttons */}
                                <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-100">
                                    <button
                                        onClick={() => handleDelete(appointment.id)}
                                        className="px-3 py-1.5 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg text-xs font-semibold hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-sm hover:shadow-md flex items-center gap-1"
                                    >
                                        <TrashIcon className="h-4 w-4" />
                                        Delete
                                    </button>
                                    <button
                                        onClick={() => handleOpenRescheduleModal(appointment)}
                                        className="px-3 py-1.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-lg text-xs font-semibold hover:from-amber-600 hover:to-orange-700 transition-all duration-200 shadow-sm hover:shadow-md flex items-center gap-1"
                                    >
                                        <ClockIcon className="h-4 w-4" />
                                        Reschedule
                                    </button>
                                    <button
                                        onClick={() => handleOpenEditNotesModal(appointment)}
                                        className="px-3 py-1.5 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg text-xs font-semibold hover:from-indigo-600 hover:to-purple-700 transition-all duration-200 shadow-sm hover:shadow-md flex items-center gap-1"
                                    >
                                        <PencilIcon className="h-4 w-4" />
                                        Edit Notes
                                    </button>
                                    <button
                                        onClick={() => handleJobCardCreate(appointment)}
                                        className="px-3 py-1.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg text-xs font-semibold hover:from-green-600 hover:to-emerald-700 transition-all duration-200 shadow-sm hover:shadow-md flex items-center gap-1"
                                    >
                                        <PlusCircleIcon className="h-4 w-4" />
                                        Create Job
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>

        {/* Modals */}
        {isRescheduleModalOpen && selectedAppointment && (
            <RescheduleAppointmentModal
                isOpen={isRescheduleModalOpen}
                onClose={() => setIsRescheduleModalOpen(false)}
                appointmentId={selectedAppointment.id}
                currentAppointmentDate={selectedAppointment.date}
                currentAppointmentTime={selectedAppointment.time}
            />
        )}

        {isEditNotesModalOpen && selectedAppointment && (
            <EditNotesModal
                isOpen={isEditNotesModalOpen}
                onClose={() => setIsEditNotesModalOpen(false)}
                appointmentId={selectedAppointment.id}
                currentNotes={selectedAppointment.notes}
            />
        )}
        </>
    );
    
};

export default AppointmentCalendar;
